import React, { useCallback, useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import {
  storefrontDomainsApi,
  type StorefrontDomainRow,
  type StorefrontDomainStatus,
} from '../../api/storefrontDomains';
import { Card } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { PageHeader } from '../../components/ui/PageHeader';
import { Spinner } from '../../components/ui/Spinner';

const STATUS: Record<
  StorefrontDomainStatus,
  { label: string; variant: 'success' | 'warning' | 'danger' }
> = {
  active: { label: 'Live', variant: 'success' },
  pending: { label: 'Waiting on DNS', variant: 'warning' },
  misconfigured: { label: 'DNS moved away', variant: 'danger' },
};

const RECORD: Record<string, string> = {
  ok: 'connected',
  missing: 'not found',
  elsewhere: 'points elsewhere',
};

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '—';
}

/**
 * Every shop on its own domain, and where each one's DNS stands.
 *
 * The record-by-record findings are what support needs: "your TXT record
 * isn't showing" is an answer, "it's pending" is not.
 */
export const StorefrontDomains: React.FC = () => {
  usePage('Shop Domains');
  const toast = useToast();

  const [rows, setRows] = useState<StorefrontDomainRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState<StorefrontDomainRow | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await storefrontDomainsApi.list());
    } catch (err) {
      toast.error(err, 'Could not load shop domains.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  const remove = async () => {
    if (!removing) return;
    setSaving(true);
    try {
      await storefrontDomainsApi.remove(removing.id);
      toast.success(`${removing.hostname} removed. The shop is back on payiq.site only.`);
      setRemoving(null);
      await fetchRows();
    } catch (err) {
      toast.error(err, 'Could not remove that domain.');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      key: 'hostname',
      header: 'Domain',
      render: (r: StorefrontDomainRow) => (
        <div>
          <a
            href={`https://${r.hostname}`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--blue-600)', fontWeight: 600 }}
          >
            {r.hostname}
            <ExternalLink size={13} />
          </a>
          <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
            {r.apex ? `${r.apex} redirects here` : 'Subdomain'}
          </div>
        </div>
      ),
    },
    {
      key: 'shop',
      header: 'Shop',
      render: (r: StorefrontDomainRow) => r.shopName ?? '—',
    },
    {
      key: 'status',
      header: 'Status',
      render: (r: StorefrontDomainRow) => (
        <Badge variant={STATUS[r.status].variant}>{STATUS[r.status].label}</Badge>
      ),
    },
    {
      key: 'records',
      header: 'Records',
      render: (r: StorefrontDomainRow) => {
        const f = r.lastFindings;
        if (!f) return '—';
        return (
          <div style={{ fontSize: 12, lineHeight: 1.6 }}>
            <div>Shop: {RECORD[f.host]}{f.host === 'elsewhere' ? ` (${f.hostSeen.join(', ')})` : ''}</div>
            {f.apex ? (
              <div>Bare domain: {RECORD[f.apex]}{f.apex === 'elsewhere' ? ` (${f.apexSeen.join(', ')})` : ''}</div>
            ) : null}
            <div>Ownership: {RECORD[f.ownership]}</div>
          </div>
        );
      },
    },
    {
      key: 'checked',
      header: 'Last checked',
      render: (r: StorefrontDomainRow) => (
        <span style={{ fontSize: 12 }}>{when(r.lastCheckedAt)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (r: StorefrontDomainRow) => (
        <Button
          variant="ghost"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            setRemoving(r);
          }}
        >
          Remove
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Shop Domains"
        description="Businesses' own domains for their storefronts, and where each one's DNS stands"
      />
      <Card>
        {loading ? (
          <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
            <Spinner />
          </div>
        ) : (
          <Table columns={columns} data={rows} emptyMessage="No shop has connected a domain yet" />
        )}
      </Card>

      <Modal
        isOpen={removing !== null}
        onClose={() => setRemoving(null)}
        title={`Remove ${removing?.hostname ?? ''}?`}
      >
        <p style={{ fontSize: 14, lineHeight: 1.5, marginTop: 0 }}>
          {removing?.shopName ?? 'The shop'} stops being served on this domain at once,
          and its payiq.site address carries on. The business can connect it again
          from the app, which means proving ownership again.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={() => setRemoving(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => void remove()} disabled={saving}>
            {saving ? 'Removing…' : 'Remove domain'}
          </Button>
        </div>
      </Modal>
    </div>
  );
};
